import { useState } from "react";
import { useList, useTranslate, Authenticated } from "@refinedev/core";
import { Space, Typography, Flex, Result, Tooltip } from "antd";
import { UserInviteForm } from "./components/UserInviteForm";
import { UserInvitesTable } from "./components/UserInvitesTable";
import { UserInviteResponse } from "types/";
import { List } from "@refinedev/antd";
import { CreatePrimaryButton } from "components/CreatePrimaryButton/";
import { QuotaWarning } from "components/QuotaWarning/";
import { useQuota } from "hooks/useQuota/";
import { QuotaFeatures } from "@/constants";
import { useSession } from "hooks/useSession/";

const UserInvitesPage = () => {
  const t = useTranslate();
  const { isBlocked, refetch: refetchQuota } = useQuota();
  const { currentOrganization } = useSession();
  const organizationId = currentOrganization?.id;
  const [editingInvite, setEditingInvite] = useState<UserInviteResponse | null>(
    null
  );
  const [showForm, setShowForm] = useState(false);

  const { data: invitesData, isLoading: isLoadingInvites } =
    useList<UserInviteResponse>({
      resource: `api/${organizationId}/user-invites`,
      queryOptions: {
        enabled: !!organizationId,
      },
    });

  const handleNewInvite = () => {
    setEditingInvite(null);
    setShowForm(true);
  };

  const handleCancel = () => {
    setEditingInvite(null);
    setShowForm(false);
  };

  const handleEdit = (record: UserInviteResponse) => {
    setEditingInvite(record);
    setShowForm(true);
  };

  const handleSuccess = () => {
    setEditingInvite(null);
    setShowForm(false);
    refetchQuota();
  };

  const reachedQuotaLimit = isBlocked(QuotaFeatures.UserLicenseAssignment);

  if (!organizationId) {
    return (
      <Authenticated key="user-invites-page">
        <Result
          status="warning"
          title={t("pages.userInvites.noOrganizationSelected")}
          subTitle={t("pages.userInvites.pleaseSelectOrganization")}
        />
      </Authenticated>
    );
  }

  const inviteCount = invitesData?.data?.length ?? 0;

  return (
    <Authenticated key="user-invites-page">
      <List
        resource="user_invite"
        title={t("pages.userInvites.userInvitationManagement")}
        headerButtons={
          <Space size="middle">
            {inviteCount > 0 && (
              <Typography.Text type="secondary">
                {t("pages.userInvites.activeInvitations", {
                  count: inviteCount,
                })}
              </Typography.Text>
            )}
            <Tooltip
              title={
                reachedQuotaLimit
                  ? t("pages.userInvites.quotaReached")
                  : undefined
              }
            >
              <CreatePrimaryButton
                onClick={handleNewInvite}
                disabled={showForm || reachedQuotaLimit}
                resource="user_invite"
                size="large"
              >
                {t("pages.userInvites.sendNewInvitation")}
              </CreatePrimaryButton>
            </Tooltip>
          </Space>
        }
      >
        <Flex vertical gap={16}>
          <QuotaWarning feature={QuotaFeatures.UserLicenseAssignment} />
          {showForm && (
            <UserInviteForm
              key={editingInvite ? editingInvite.id : "new-invite"}
              organizationId={organizationId}
              editingInvite={editingInvite}
              onCancel={handleCancel}
              onSuccess={handleSuccess}
            />
          )}

          <UserInvitesTable
            organizationId={organizationId}
            invitesData={invitesData?.data}
            isLoading={isLoadingInvites}
            onEdit={handleEdit}
            onSendFirst={reachedQuotaLimit ? undefined : handleNewInvite}
          />
        </Flex>
      </List>
    </Authenticated>
  );
};

export default UserInvitesPage;
